import axiosInstance from '@/lib/axios';
import axios from 'axios';

export interface FinancialReportItem {
	 id?: string;
	 _id?: string;
	 description: string;
	 category?: string;
	 amount: number;
	 subtotal?: number;
	 date: string;
	 type?: 'income' | 'expense' | string;
}

export interface FinancialReportsFilters {
	 search?: string;
	 type?: string;
	 dateFrom?: string;
	 dateTo?: string;
	 page?: number;
	 limit?: number;
}

export interface FinancialReportsResponse {
	 data: FinancialReportItem[];
	 total: number;
	 page: number;
	 limit: number;
	 hasMore: boolean;
}

export interface FinancialReportDownload {
	 blob: Blob;
	 fileName: string;
}

class FinancialReportsService {
	 private readonly endpoint: string;
	 private readonly exportPdfEndpoint: string;

	 constructor(endpoint?: string) {
		 const envEndpoint = import.meta.env.VITE_FINANCIAL_REPORTS_ENDPOINT as string | undefined;
		 this.endpoint = endpoint || envEndpoint || '/reports/transactions';
		 const envExportEndpoint = import.meta.env.VITE_FINANCIAL_REPORTS_EXPORT_ENDPOINT as string | undefined;
		 this.exportPdfEndpoint = envExportEndpoint || `${this.endpoint}/export/pdf`;
	 }

	 async list(filters: FinancialReportsFilters = {}): Promise<FinancialReportsResponse> {
		 const queryString = this.buildQueryString(filters);
		 const url = queryString ? `${this.endpoint}?${queryString}` : this.endpoint;
		 const response = await axiosInstance.get(url);
		 const payload = response.data;
		 if (payload?.success && Array.isArray(payload.data)) {
			 return {
				 data: payload.data,
				 total: payload.pagination?.total ?? payload.data.length,
				 page: payload.pagination?.page ?? 1,
				 limit: payload.pagination?.limit ?? payload.data.length,
				 hasMore: payload.pagination?.hasNextPage ?? false,
			 };
		 }
		 if (Array.isArray(payload)) {
			 return { data: payload, total: payload.length, page: 1, limit: payload.length, hasMore: false };
		 }
		 return {
			 data: (payload?.data as FinancialReportItem[]) ?? [],
			 total: (payload?.total as number) ?? ((payload?.data?.length as number) ?? 0),
			 page: (payload?.page as number) ?? 1,
			 limit: (payload?.limit as number) ?? ((payload?.data?.length as number) ?? 0),
			 hasMore: (payload?.hasMore as boolean) ?? false,
		 };
	 }

	 async exportPdf(filters: FinancialReportsFilters = {}): Promise<FinancialReportDownload> {
		 const queryString = this.buildQueryString(filters);
		 const url = queryString ? `${this.exportPdfEndpoint}?${queryString}` : this.exportPdfEndpoint;
		 try {
			 const response = await axiosInstance.get<Blob>(url, {
				 responseType: 'blob',
				 headers: { Accept: 'application/pdf' },
				});
			 const contentType = String(response.headers?.['content-type'] ?? '').toLowerCase();
			 const isPdf = contentType.includes('application/pdf') || contentType.includes('application/octet-stream') || contentType === '';
			 if (!isPdf) {
				 const message = await this.extractErrorMessage(response.data);
				 throw new Error(message ?? 'Financial report export returned an unexpected response.');
			 }
			 const dispositionRaw = response.headers?.['content-disposition'] as string | undefined;
			 const fileName = this.resolveFileName(dispositionRaw) ?? this.buildDefaultFileName();
			 return { blob: response.data, fileName };
		 } catch (error) {
			 const message = await this.resolveExportError(error);
			 throw new Error(message);
		 }
	 }

	 private buildQueryString(filters: FinancialReportsFilters): string {
		 const params = new URLSearchParams();
		 Object.entries(filters).forEach(([key, value]) => {
			 if (value !== undefined && value !== null && value !== '') {
				 params.append(key, String(value));
			 }
		 });
		 return params.toString();
	 }

	 private resolveFileName(contentDisposition?: string): string | undefined {
		 if (!contentDisposition) {
			 return undefined;
		 }
		 const encodedMatch = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(contentDisposition);
		 if (encodedMatch?.[1]) {
			 try {
				 return decodeURIComponent(encodedMatch[1].trim().replace(/['"]/g, ''));
			 } catch {
			 }
		 }
		 const asciiMatch = /filename="?([^";]+)"?/i.exec(contentDisposition);
		 return asciiMatch?.[1]?.trim();
	 }

	 private buildDefaultFileName(): string {
		 const timestamp = new Date();
		 const pad = (value: number) => value.toString().padStart(2, '0');
		 const formatted = `${timestamp.getFullYear()}${pad(timestamp.getMonth() + 1)}${pad(timestamp.getDate())}_${pad(timestamp.getHours())}${pad(timestamp.getMinutes())}${pad(timestamp.getSeconds())}`;
		 return `financial-report-${formatted}.pdf`;
	 }

	 private async resolveExportError(error: unknown): Promise<string> {
		 if (axios.isAxiosError(error)) {
			 const responseData = error.response?.data;
			 const messageFromResponse = await this.extractErrorMessage(responseData);
			 if (messageFromResponse) {
				 return messageFromResponse;
			 }
			 if (typeof error.message === 'string' && error.message.trim()) {
				 return error.message;
			 }
		 } else if (error instanceof Error && error.message.trim()) {
			 return error.message;
		 }
		 return 'Unable to export the financial report. Please try again later.';
	 }

	 private async extractErrorMessage(payload: unknown): Promise<string | undefined> {
		 if (!payload) {
			 return undefined;
		 }
		 if (typeof payload === 'string') {
			 return payload;
		 }
		 if (payload instanceof Blob) {
			 try {
				 const text = await payload.text();
				 return this.parseErrorText(text);
			 } catch {
				 return undefined;
			 }
		 }
		 if (typeof payload === 'object') {
			 const record = payload as Record<string, unknown>;
			 for (const key of ['message', 'error', 'detail', 'description']) {
				 const value = record[key];
				 if (typeof value === 'string' && value.trim()) {
					 return value;
				 }
			 }
			}
		 return undefined;
	 }

	 private parseErrorText(text: string): string | undefined {
		 if (!text.trim()) {
			 return undefined;
		 }
		 try {
			 const parsed = JSON.parse(text) as Record<string, unknown>;
			 for (const key of ['message', 'error', 'detail', 'description']) {
				 const value = parsed[key];
				 if (typeof value === 'string' && value.trim()) {
					 return value;
				 }
			 }
		 } catch {
			 // Ignore JSON parsing errors and fall back to raw text
		 }
		 return text;
	 }
}

export const financialReportsService = new FinancialReportsService();
